/**
 * The guest list as a spreadsheet.
 *
 * The PDF is for the day itself; this is for everyone who asks the couple for
 * "the list" beforehand — the venue, the caterer, the couple's own spreadsheet.
 * It is the plan read out in table order, which is the order those people work
 * in: who sits where, what they answered, and any note about them.
 *
 * Pure, so it is tested like the rest of `lib/`; handing the text to the
 * browser is the caller's business (`saveBlob`).
 */

import type { Catalogue } from './i18n'
import type { Guest, Plan } from './types'

/**
 * Excel reads a UTF-8 file as the machine's 8-bit codepage unless it opens with
 * a byte-order mark, which turns Azerbaijani, Russian and Arabic names into
 * mojibake. Every catalogue here has such names in it, so the mark is not
 * optional.
 */
const BOM = '﻿'

/** RFC 4180's line ending, which is also the one Excel expects. */
const CRLF = '\r\n'

/**
 * A spreadsheet runs a cell that starts with `=`, `+`, `-`, `@`, a tab or a
 * carriage return as a formula — and names, groups, notes and table names can
 * come from someone else's backup or a pasted list, so `=HYPERLINK(…)` would be
 * a live link in the venue's sheet. An apostrophe in front makes it text (and
 * shows, the usual price). Also after a `;` or a line break: a reader that
 * splits on `;` — Excel, wherever a comma is the decimal mark — starts a cell
 * at either.
 */
const FORMULA = /(^|[;\r\n])([ \u00a0]*)(?=[=+\-@\t\r])/g

/**
 * A field is quoted when it holds a separator, a quote, a line break, or edge
 * whitespace a reader would otherwise trim; an inner quote is doubled.
 */
function field(raw: string): string {
  const value = raw.replace(FORMULA, "$1$2'")
  if (!/[",\r\n]/.test(value) && value.trim() === value) return value
  return `"${value.replace(/"/g, '""')}"`
}

const line = (fields: string[]): string => fields.map(field).join(',')

function rsvpWord(guest: Guest, t: Catalogue): string {
  if (guest.rsvp === 'yes') return t.inspector.rsvpComing
  if (guest.rsvp === 'no') return t.inspector.rsvpCant
  return t.inspector.rsvpPending
}

const row = (guest: Guest, table: string, seat: string, t: Catalogue): string[] => [
  guest.fullName,
  guest.groupLabel ?? '',
  rsvpWord(guest, t),
  table,
  seat,
  guest.note ?? '',
]

export function guestListCsv(plan: Plan, t: Catalogue): string {
  const byId = new Map(plan.guests.map((guest) => [guest.id, guest]))
  const seated = new Set<string>()
  const rows: string[][] = []

  for (const table of plan.tables) {
    for (const seat of [...table.seats].sort((a, b) => a.position - b.position)) {
      const guest = seat.guestId ? byId.get(seat.guestId) : undefined
      if (!guest) continue
      seated.add(guest.id)
      // Seats are numbered from one on paper, as they are everywhere else.
      rows.push(row(guest, table.name, String(seat.position + 1), t))
    }
  }

  // Whoever is still waiting keeps the order the roster holds them in — the
  // order they were typed or pasted, which is the couple's own list.
  for (const guest of plan.guests) {
    if (!seated.has(guest.id)) rows.push(row(guest, t.csv.notSeated, '', t))
  }

  const header = [
    t.inspector.guestNameLabel,
    t.inspector.groupLabel,
    t.inspector.rsvpLabel,
    t.csv.table,
    t.csv.seat,
    t.inspector.noteLabel,
  ]
  return BOM + [header, ...rows].map(line).join(CRLF)
}
